import { BehaviorSubject } from 'rxjs';
import { UserRoles } from '../types';
import { useEffect, useState } from 'react';
import { fetchRoles as apiFetchRoles } from '../Api/Admin/Roles';
import { addError } from './Error';

// Role names as upstack_api's Role spells them; a mistyped member fails to compile, a mistyped string does not.
export enum Role {
  ADMIN = 'Admin',
  SUPPORT = 'Support',
  ADVISOR = 'Advisor',
  CSM = 'Customer Success Manager',
  SDM = 'Service Delivery Manager',
  EXECUTIVE_FULL = 'Executive - Full',
  EXECUTIVE_VIEW = 'Executive - View',
  TEAM_MANAGER = 'Team Manager',
  INVENTORY_FULL = 'Inventory - Full',
  INVENTORY_VIEW = 'Inventory - View',
  SUPPORT_CASES_FULL = 'Support Cases - Full',
  SUPPORT_CASES_VIEW = 'Support Cases - View',
  ADVISE_TEAM_MANAGER = 'Advise - Team Manager',
  ADVISE_REVIEW = 'Advise - Review',
  ADVISE_CX_ANALYST = 'Advise - CX Analyst',
  ADVISE_SECURITY_ANALYST = 'Advise - Security Analyst',
  ADVISE_DATA_CENTER_ANALYST = 'Advise - Data Center Analyst',
  ADVISE_CLOUD_ANALYST = 'Advise - Cloud Analyst'
}

// Mirrors Role::ANALYST_PRACTICES in upstack_api: an Analyst's practice comes from its role.
export const ANALYST_PRACTICES: Record<string, string> = {
  [Role.ADVISE_CX_ANALYST]: 'cx',
  [Role.ADVISE_SECURITY_ANALYST]: 'security',
  [Role.ADVISE_DATA_CENTER_ANALYST]: 'data_center',
  [Role.ADVISE_CLOUD_ANALYST]: 'cloud'
};

export const ADVISE_ANALYSTS: string[] = Object.keys(ANALYST_PRACTICES);
export const ADVISE_ROLES: string[] = [Role.ADVISE_TEAM_MANAGER, Role.ADVISE_REVIEW, ...ADVISE_ANALYSTS];
// The Advise roles held by UPSTACK staff; Review is held by clients as well, so it carries no reach of its own.
export const ADVISE_STAFF_ROLES: string[] = [Role.ADVISE_TEAM_MANAGER, ...ADVISE_ANALYSTS];

// Mirrors Role::FEATURE_ROLES in upstack_api: the roles that open a feature to a user whose reach is from Advise.
export const FEATURE_ROLES: Record<'inventory' | 'cases', { read: string[]; write: string[] }> = {
  inventory: { read: [Role.INVENTORY_FULL, Role.INVENTORY_VIEW], write: [Role.INVENTORY_FULL] },
  cases: { read: [Role.SUPPORT_CASES_FULL, Role.SUPPORT_CASES_VIEW], write: [Role.SUPPORT_CASES_FULL] }
};

// Roles held by UPSTACK internal staff. Mirrors Role::INTERNAL_ROLES in
// upstack_api (app/models/role.rb), whose server-side checks read the same set;
// the two must stay in sync.
export const INTERNAL_ROLES: string[] = [
  Role.ADMIN,
  Role.SUPPORT,
  Role.EXECUTIVE_FULL,
  Role.EXECUTIVE_VIEW,
  Role.ADVISOR,
  Role.CSM,
  Role.SDM,
  ...ADVISE_STAFF_ROLES
];

// A user holding an internal role sits on no single account.
export const SKIP_ACCOUNT_ROLES: string[] = INTERNAL_ROLES;

export const userInSkipRoles = (userRoles: string[]) => {
  return userRoles.some((role) => SKIP_ACCOUNT_ROLES.includes(role));
};

// These mirror Role::ALL_ACCOUNT_*_ROLES in upstack_api, which match role names, not permissions.
export const ALL_ACCOUNT_VIEW_ROLES: string[] = [
  Role.ADMIN,
  Role.SUPPORT,
  Role.EXECUTIVE_FULL,
  Role.EXECUTIVE_VIEW,
  Role.CSM,
  Role.SDM
];
export const ALL_ACCOUNT_WRITE_ROLES: string[] = [Role.ADMIN, Role.SUPPORT, Role.EXECUTIVE_FULL, Role.CSM, Role.SDM];

export interface UseRolesReturn {
  setRolesData: (accounts: UserRoles[]) => void;
  fetchRoles: () => Promise<void>;
  clear: () => void;
  roles: UserRoles[];
  rolesLoading: boolean;
}

const defaultRolesData: UserRoles[] = [];
const subject = new BehaviorSubject<UserRoles[]>(defaultRolesData);
const emitErrorState = (message?: string): void => addError('role', message);

export const getRoleSubject = (): BehaviorSubject<UserRoles[]> => subject;
export const getRoles = (): UserRoles[] => subject.value;

export const useRoles = (): UseRolesReturn => {
  const [rolesLoading, setRolesLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<UserRoles[]>(defaultRolesData);

  const fetchRoles = async (): Promise<void> => {
    setRolesLoading(true);
    const { data, error } = await apiFetchRoles();

    if (data) {
      emitErrorState();
      setRolesData(data);
    }

    if (error) {
      setRolesData(defaultRolesData);
      emitErrorState(error.message);
    }

    setRolesLoading(false);
  };

  useEffect(() => {
    const rolesSub = subject.subscribe((roles) => setRoles(roles));

    return () => {
      if (rolesSub) rolesSub.unsubscribe();
    };
  }, []);

  const setRolesData = (roles: UserRoles[]) => subject.next(roles);
  const clear = () => setRolesData(defaultRolesData);

  return { setRolesData, fetchRoles, clear, roles, rolesLoading };
};
